import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Grovs from "grovs";
import LocalStorage from "../Helpers/LocalStorage";

export const LinksquaredContext = createContext({
  linksquared: [],
  linksquaredUnread: [],
  linksquaredReceivedData: [],
  isDeepLinkAvailable: [],
  deepLink: [],
  getNumberOfUnreadLinksquaredMessages: () => {},
  markLinksquaredMessageAsRead: () => {},
  setUserDetails: () => {},
  generateLink: () => {},
  getAllReceivedData: () => {},
  setIsDeepLinkAvailable: () => {},
  setDeepLink: () => {},
});

const LinksquaredContextProvider = ({ children }) => {
  const [linksquaredUnread, setLinksquaredUnred] = useState(0);
  const [linksquaredReceivedData, setLinksquaredReceivedData] = useState(null);

  const [isDeepLinkAvailable, setIsDeepLinkAvailable] = useState(false);
  const [deepLink, setDeepLink] = useState(null);
  const testMode = process.env.REACT_APP_GROVS_TEST_MODE === "true";
  const linksquared = useRef();

  const getNumberOfUnreadLinksquaredMessages = useCallback(() => {
    linksquared.current.getNumberOfUnreadMessages(
      (response) => {
        setLinksquaredUnred(response.number_of_unread_notifications);
      },
      (error) => {
        console.log(error);
      }
    );
  }, []);

  const markLinksquaredMessageAsRead = useCallback(
    (message) => {
      linksquared.current.markMessageAsRead(
        message,
        () => {
          getNumberOfUnreadLinksquaredMessages();
        },
        (error) => {
          console.log(error);
        }
      );
    },
    [getNumberOfUnreadLinksquaredMessages]
  );

  const setUserDetails = useCallback((user) => {
    linksquared.current.setUserIdentifier(user.id);
    linksquared.current.setUserAttributes({ name: user.name });
  }, []);

  const getAllReceivedData = useCallback((dataCallback) => {
    const data = linksquared.current.getAllReceivedData();
    dataCallback(data);
  }, []);

  const getInvitedBy = useCallback(() => {
    const dataArray = linksquared.current.getAllReceivedData();

    dataArray.forEach((dict) => {
      if (dict["invited_by"] != null) {
        LocalStorage.setInvitedBy(dict["invited_by"]);
      }
    });

    return LocalStorage.getInvitedBy();
  }, []);

  const generateLink = useCallback((title, subtitle, user, success, error) => {
    linksquared.current.createLink(
      title,
      subtitle,
      null,
      { invited_by: `${user.id}` },
      success,
      error
    );
  }, []);

  useEffect(() => {
    linksquared.current = new Grovs(
      process.env.REACT_APP_GROVS_PROJECT_KEY || "emotii_2fb260c812b350d41e4268f59362ad13f5c4a84011ded882973425c5a26a5026",
      testMode,
      (data) => {
        setLinksquaredReceivedData(data);
      }
    );
    linksquared.current.start();
    getNumberOfUnreadLinksquaredMessages();
  }, []);

  const contextValue = useMemo(
    () => ({
      linksquared,
      linksquaredUnread,
      linksquaredReceivedData,
      getNumberOfUnreadLinksquaredMessages,
      markLinksquaredMessageAsRead,
      setUserDetails,
      generateLink,
      getAllReceivedData,
      getInvitedBy,
      isDeepLinkAvailable,
      setIsDeepLinkAvailable,
      deepLink,
      setDeepLink,
    }),
    [
      linksquaredUnread,
      linksquaredReceivedData,
      getNumberOfUnreadLinksquaredMessages,
      markLinksquaredMessageAsRead,
      setUserDetails,
      generateLink,
      getAllReceivedData,
      getInvitedBy,
      isDeepLinkAvailable,
      deepLink,
    ]
  );

  return (
    <LinksquaredContext.Provider value={contextValue}>
      {children}
    </LinksquaredContext.Provider>
  );
};
export const useLinksquaredService = () => useContext(LinksquaredContext);

export default LinksquaredContextProvider;
